import { Heart } from 'lucide-react';
import { User } from '../types';
import { calculateDaysDifference, formatDate } from '../utils/dateUtils';
import { useAuth } from '../contexts/AuthContext';
import { useToastContext } from '../contexts/ToastContext';
import UserAvatar from './UserAvatar';
import './UserCard.css';

interface UserCardProps {
  user: User;
  forceStampCompleted?: boolean;
  onShowPRModal?: (user: User) => void;
}

export default function UserCard({ user, forceStampCompleted = false, onShowPRModal }: UserCardProps) {
  const { likeUser, hasLikedUser, currentUser } = useAuth();
  const { showSuccess, showError } = useToastContext();
  const daysSinceStart = calculateDaysDifference(user.themeStartDate);
  const today = formatDate(new Date());
  const hasStampToday = forceStampCompleted || user.stamps.includes(today);
  const hasLiked = hasLikedUser(user.id);
  const isOwnCard = currentUser?.id === user.id;

  const handleAvatarClick = () => {
    if (user.companyName || user.businessContent) {
      onShowPRModal?.(user);
    }
  };

  const handleLike = async () => {
    if (isOwnCard) {
      showError('自分にはいいねできません');
      return;
    }

    if (hasLiked) {
      showError('今日は既にいいねしています');
      return;
    }

    const success = await likeUser(user.id);
    if (success) {
      showSuccess(`${user.name}さんにいいねしました！`);
    } else {
      showError('いいねに失敗しました');
    }
  };

  return (
    <div className="user-card">
      <div className="user-card-header">
        <div 
          className={user.companyName || user.businessContent ? 'user-avatar-clickable' : ''}
          onClick={handleAvatarClick}
        >
          <UserAvatar size={60} photoUrl={user.profilePhoto} />
        </div>
        <div className="user-info">
          <h3 className="user-name">{user.name}</h3>
          <p className="days-count">{daysSinceStart}日目</p>
        </div>
        <button 
          className={`like-button ${hasLiked ? 'like-button-liked' : ''} ${isOwnCard ? 'like-button-disabled' : ''}`} 
          onClick={handleLike}
          disabled={isOwnCard}
        >
          <Heart size={24} fill={hasLiked ? '#ff0000' : 'none'} />
          <span className="like-count">{user.likes}</span>
        </button>
      </div>

      <div className="theme-container">
        <p className="theme">{user.theme}</p>
      </div>

      <div className="stamp-status-container">
        <div className="stamp-status">
          <div className={`stamp-indicator ${hasStampToday ? 'stamp-indicator-active' : ''}`} />
          <span className="stamp-status-text">
            {hasStampToday ? '今日も実践しました！' : '今日のスタンプ未完了'}
          </span>
        </div>
      </div>
    </div>
  );
}